import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
  Divider,
  Box,
  DialogTitle,
  CircularProgress,
} from '@material-ui/core';
import parse from 'html-react-parser';
import CollectionContactsTable from '../dialogs/CollectionContactsTable';
import { Table } from '../tables';
import { SEND_COLLECTION_MAIL, GET_COLLECTION_MAIL_ENUM, GET_COLLECTION_MAIL_PREVIEW } from '../../gql/collection';
import { OneButtonAlert } from '../dialogs';

const headers = [
  { id: 'folio', label: 'Folio', align: 'left' },
  { id: 'client', label: 'Cliente', align: 'left' },
  { id: 'amount', label: 'Monto', align: 'right' },
  { id: 'paymentDate', label: 'Fecha de Pago', align: 'left' },
  { id: 'debt', label: 'Mora acumulada', align: 'right' },
  { id: 'timeInStage', label: 'Tiempo en esta Etapa', align: 'right' },
  { id: 'integrationStatus', label: 'Estado en SII', align: 'right' },
];

const useStyles = makeStyles({
  tableContainer: {
    maxWidth: '80%',
    marginInline: 'auto',
  },
  titles: {
    marginTop: '2%',
    marginBottom: '2%',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendMailButton: {
    backgroundColor: '#404BD0',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1a237e',
    },
  },
});

const CollectionSendMail = ({ open, setOpen, selectedInvoices, nameReceiver, rutReceiver }) => {
  const { data: mailTypeOptionsData } = useQuery(GET_COLLECTION_MAIL_ENUM);
  const classes = useStyles();
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [mailType, setMailType] = useState();
  const [mailTypeOptions, setMailTypeOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [successResponse, setSuccessResponse] = useState(false);
  const [successPreviewResponse, setSuccessPreviewResponse] = useState(false);
  const [openFinalDialog, setOpenFinalDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [mailPreview, setMailPreview] = useState('');
  const [SendCollectionMail] = useMutation(SEND_COLLECTION_MAIL);
  const [GetCollectionMailPreview] = useMutation(GET_COLLECTION_MAIL_PREVIEW);
  const [mailInvoices, setMailInvoices] = useState(
    selectedInvoices.map((inv) => ({
      managerId: inv.collectionManagerId,
      folio: inv.folio,
      amount: inv.amount,
      paymentDate: inv.paymentDate,
      integrationStatus: inv.integrationStatus,
      debt: inv.debt,
      client: inv.client,
      timeInStage: inv.timeInStage,
    })),
  );
  useEffect(() => {
    if (mailTypeOptionsData && mailTypeOptionsData.getCollectionMailEnum) {
      setMailTypeOptions(mailTypeOptionsData.getCollectionMailEnum);
    }
  }, [mailTypeOptionsData]);
  const handleMailTypeChange = (e) => {
    setMailType(e.target.value);
  };
  const handleSelect = (_selectedRows) => {
    setSelectedContacts(_selectedRows.map((row) => ({
      id: row.id,
      name: row.name.value,
    })));
  };

  const handlePreview = async () => {
    if (mailType == null) {
      setErrorMessage('Debe seleccionar un tipo de correo');
      return;
    }
    setLoading(true);
    const { data: {
      getCollectionMailPreview: { success, preview } } } = await GetCollectionMailPreview({
      variables: {
        mailType,
        managersIds: mailInvoices.map((inv) => inv.managerId),
      },
    });
    setMailPreview(preview);
    setSuccessPreviewResponse(success);
    setOpenPreviewDialog(true);
    setLoading(false);
  };

  const handleSendMailButton = async () => {
    if (selectedContacts.length <= 0) {
      setErrorMessage('Debes seleccionar al menos un contacto');
      return;
    }
    if (mailType == null) {
      setErrorMessage('Debe seleccionar un tipo de correo');
      return;
    }
    setLoading(true);
    const { data: { sendCollectionMail: { success } } } = await SendCollectionMail({
      variables: {
        mailType,
        contactsIds: selectedContacts.map((contact) => contact.id),
        managersAndAttachs: mailInvoices.map((inv) => ({ managerId: inv.managerId })),
      },
    });
    setOpenFinalDialog(true);
    setSuccessResponse(success);
    setLoading(false);
  };
  const handleFinalDialogClose = () => {
    setOpen(false);
    setOpenFinalDialog(false);
    window.location.reload();
  };
  const transformDataToComponents = (_data) => _data.map((row) => {
    const {
      managerId,
      folio,
      paymentDate,
      amount,
      integrationStatus,
      debt,
      client,
      timeInStage,
    } = row;
    return ({
      managerId,
      debt,
      folio,
      amount,
      paymentDate,
      integrationStatus,
      client,
      timeInStage,
    });
  });
  return (
    <>
      <OneButtonAlert
        open={openFinalDialog}
        onClose={handleFinalDialogClose}
        message={(successResponse) ? 'Mails enviados con éxito.' : 'No logramos enviar los mails, contactate con el equipo de desarrollo.'}
        title="Envío de mails"
      />
      <OneButtonAlert
        open={openPreviewDialog}
        message={(successPreviewResponse) ? parse(mailPreview) : 'No logramos cargar el preview, contactate con el equipo de desarrollo.'}
        title="Preview"
        onClose={() => setOpenPreviewDialog(false)}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          <Typography variant="h4" align="center">Enviar correo a {nameReceiver}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" align="center" className={classes.titles}>Selecciona el tipo de correo</Typography>
          <RadioGroup
            aria-label="rejection-reason"
            value={mailType}
            onChange={handleMailTypeChange}
            row
          >
            {mailTypeOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          <Divider />
          <Typography variant="h6" align="center" className={classes.titles}>Selecciona los contactos</Typography>
          <CollectionContactsTable
            companyRut={rutReceiver}
            companyName={nameReceiver}
            onSelect={handleSelect}
          />
          <Divider />
          <Typography variant="h6" align="center" className={classes.titles}><></>Información del correo</Typography>
          <Box className={classes.tableContainer}>
            <Table
              withSelect={false}
              pagination={false}
              headers={headers}
              data={mailInvoices}
              dataToRows={transformDataToComponents}
              setNewDataOrder={setMailInvoices}
              initialOrderId="id"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Typography color="error" variant="h6">{errorMessage}</Typography>
          { (loading) && (
            <CircularProgress />
          )}
          <Button variant="contained" color="success" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handlePreview}>
            Ver correo
          </Button>
          <Button variant="contained" color="inherit" className={classes.sendMailButton} onClick={handleSendMailButton}>
            Enviar correo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

CollectionSendMail.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedInvoices: PropTypes.objectOf(PropTypes.object).isRequired,
  nameReceiver: PropTypes.string.isRequired,
  rutReceiver: PropTypes.string.isRequired,
};

export default CollectionSendMail;
